import { PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import '../common.i18n';
import { Chip, Skeleton, StateView } from '@hana/ui';
import { StateCard, meeStateProps } from '../meeState';
import { IconExternalLink, IconSchool } from '@tabler/icons-react';
import { MeeHeaderTools } from '../Parts';
import { useMee } from '../useMee';
import s from '../mee.module.css';
import { KPI_PERIOD, courseDueCount, coursesOf, kpisOf, type Course } from './growData';

const RING_R = 18;
const RING_LEN = 2 * Math.PI * RING_R;
const TONE_VAR = { ok: 'var(--ok)', warn: 'var(--warn)', err: 'var(--err)' } as const;
const BADGE_TONE: Record<Course['tone'], 'ok' | 'warn' | 'err' | 'info' | 'neutral'> = { ok: 'ok', warn: 'warn', err: 'err', pri: 'info', neutral: 'neutral' };

function GrowBody({ dept }: { dept: string }) {
  const t = useT();
  const kpis = kpisOf(dept);
  const courses = coursesOf();
  return (
    <div className={`${s.page} ${s.growGrid}`}>
      <section className={s.card} aria-label="KPI cá nhân">
        <div className={s.head}>
          <b>KPI {KPI_PERIOD}</b>
          <span className={s.info} title="Đo bằng số job, đúng hạn, trạng thái · bộ chỉ tiêu theo ghế" aria-label="Đo bằng số job, đúng hạn, trạng thái · bộ chỉ tiêu theo ghế">ⓘ</span>
          <a className={`${s.link} ${s.right}`} href="/ezy" target="_blank" rel="noopener">
            {t('mc.ezyDetail')}
          </a>
        </div>
        {kpis.map((k) => (
          <div key={k.k} className={s.kpi}>
            <div className={s.kpiTop}>
              <span>{k.k}</span>
              <b style={{ color: TONE_VAR[k.tone] }}>{k.v}</b>
              <span className={s.kpiGoal}>/ {k.goal}</span>
            </div>
            <div className={s.kpiBar} role="img" aria-label={k.aria}>
              <i style={{ width: `${k.w.toFixed(1)}%`, background: TONE_VAR[k.tone] }} />
              <u style={{ left: `${k.gl.toFixed(1)}%` }} />
            </div>
          </div>
        ))}
      </section>
      <section className={s.card} aria-label={t('mc.courses')}>
        <div className={s.head}>
          <b>{t('mc.courses')}</b>
          <Chip tone="err" label={t('mc.mandatory')}>{courseDueCount(courses)}</Chip>
        </div>
        {courses.map((c) => (
          <div key={c.k} className={s.course}>
            <svg aria-hidden="true" width="44" height="44" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r={RING_R} fill="none" stroke="var(--line)" strokeWidth="5" />
              <circle
                cx="22" cy="22" r={RING_R} fill="none" strokeWidth="5" strokeLinecap="round" transform="rotate(-90 22 22)"
                stroke={c.p === 1 ? 'var(--ok)' : c.tone === 'err' ? 'var(--err)' : 'var(--d-blue)'}
                strokeDasharray={`${(Math.max(0.02, c.p) * RING_LEN).toFixed(1)} ${RING_LEN.toFixed(1)}`}
              />
              <text x="22" y="26" textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--tx)">{c.pct}</text>
            </svg>
            <span className={s.main}>
              <b>{c.k}</b>
              <span>{c.cat}</span>
            </span>
            <Chip tone={BADGE_TONE[c.tone]}>{c.badge}</Chip>
            <a className={s.courseGo} href="/edu" target="_blank" rel="noopener" aria-label={c.aria}>
              <IconSchool size={16} aria-hidden="true" />
              {c.cta}
              <IconExternalLink size={14} aria-hidden="true" />
            </a>
          </div>
        ))}
      </section>
    </div>
  );
}

export function GrowScreen() {
  const t = useT();
  const m = useMee();
  return (
    <>
      <PageHeader
        title="KPI & học tập"
        actions={
          m.state === 'ready' ? (
            <>
              <MeeHeaderTools />
              <a className={s.eduBtn} href="/edu" target="_blank" rel="noopener">
                <IconSchool size={18} aria-hidden="true" />
                {t('mc.openEdu')}
              </a>
            </>
          ) : undefined
        }
      />
      <StateCard state={m.state}>
        <StateView
          state={m.state}
          {...meeStateProps('grow', m.state, m.retry, () => window.open('/edu', '_blank', 'noopener'))}
          skeleton={<div className={s.skel}><Skeleton rows={4} /><Skeleton rows={4} /></div>}
        >
          <GrowBody dept={m.me?.dept ?? ''} />
        </StateView>
      </StateCard>
    </>
  );
}
